import { RealEstateDto } from "api/real-estate";
import { useCallback, useEffect, useRef, useState } from "react";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";

const PAGE_SIZE = 20;

interface Params {
  search?: string;
  type?: number | null;
}

/**
 * Liste paginée des biens d'un parc en infinite scroll (recherche + filtre type côté serveur).
 * Empile les pages ; se réinitialise quand riskId / recherche / type changent.
 */
export const useParcBiensInfinite = (riskId: string | undefined, { search, type }: Params) => {
  const [biens, setBiens] = useState<RealEstateDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(1);

  const fetchPage = useCallback(
    async (page: number, append: boolean) => {
      if (!riskId) return;
      if (append) setIsLoadingMore(true);
      else setIsLoading(true);
      try {
        const res = await realEstateAPI.searchBiensByParc({
          riskId,
          searchParcBiensCommand: {
            pageNumber: page,
            pageSize: PAGE_SIZE,
            searchString: search || undefined,
            propertyType: type ?? undefined,
          },
        });
        const items = res.items ?? [];
        const total = res.totalCount ?? 0;
        setBiens((prev) => {
          const next = append ? [...prev, ...items] : items;
          setHasMore(next.length < total);
          return next;
        });
      } catch (e) {
        console.error("Erreur chargement des biens du parc:", e);
        if (!append) {
          setBiens([]);
          setHasMore(false);
        }
      } finally {
        if (append) setIsLoadingMore(false);
        else setIsLoading(false);
      }
    },
    [riskId, search, type]
  );

  // Reset + (re)chargement page 1 à chaque changement de critère.
  useEffect(() => {
    pageRef.current = 1;
    fetchPage(1, false);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    pageRef.current += 1;
    fetchPage(pageRef.current, true);
  }, [isLoading, isLoadingMore, hasMore, fetchPage]);

  return { biens, isLoading, isLoadingMore, hasMore, loadMore };
};
